/**
 * NIRSALPortfolioReport — Portfolio Reporting (Primary SSOT input page)
 * Route: /corporate/nirsal/reporting
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { toast } from "sonner";
import client from "@/api/client";
import { Upload, Download, Save, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

const fmtN = (v, u = "") => { if (!v) return "—"; const n = Number(v); return n >= 1000 ? `${(n/1000).toFixed(0)}K ${u}`.trim() : `${n} ${u}`.trim(); };
const fmtU = (v) => { if (!v) return "—"; const n = Number(v); if (n >= 1_000_000) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1_000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };

const TECHNOLOGIES = [
  "biodigester_manure","rice_wastewater_methane","biodigester_agri","solar_irrigation",
  "solar_cold_room","solar_offgrid","composting_agri_residues","biomass_briquettes",
  "agroforestry_trees","plastic_collection_recycling","other_csa",
];
const TECH_LABELS = {
  biodigester_manure:"Biodigester — Manure/Effluent",rice_wastewater_methane:"Rice Mill Wastewater Methane",
  biodigester_agri:"Biodigester — Agri Processing",solar_irrigation:"Solar Irrigation / Pumps",
  solar_cold_room:"Solar Cold Room / Storage",solar_offgrid:"Solar Off-grid Power",
  composting_agri_residues:"Composting (Agri Residues)",biomass_briquettes:"Biomass Briquettes",
  agroforestry_trees:"Agroforestry / Trees on Farm",plastic_collection_recycling:"Plastic Collection & Recycling",
  other_csa:"Other CSA Intervention",
};
const PATHWAYS = [{v:"article6_4",l:"Article 6.4"},{v:"vcm",l:"VCM"},{v:"plastic",l:"Plastic Credits"},{v:"internal",l:"Internal Only"}];
const STATUSES = ["identified","appraised","financed","active"];

export default function NIRSALPortfolioReport() {
  const { consolidated, loading, selectedPeriod, periods, setSelectedPeriod, refresh } = useNIRSAL();
  const navigate = useNavigate();
  const [activeCluster, setActiveCluster] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("manual");
  const [expanded, setExpanded] = useState(null);

  const clusters = consolidated?.clusters || [];
  const portfolio = consolidated?.portfolio || {};

  useEffect(() => {
    if (clusters.length > 0 && !activeCluster) {
      const c = clusters[0];
      setActiveCluster(c.cluster_id);
      setForm(c);
    }
  }, [clusters]);

  const selectCluster = (cid) => {
    const c = clusters.find(x => x.cluster_id === cid);
    if (c) { setActiveCluster(cid); setForm(c); }
  };

  const handleSave = async () => {
    if (!activeCluster || !selectedPeriod) return;
    setSaving(true);
    try {
      await client.post(`/nirsal/cluster/${activeCluster}/${selectedPeriod}`, form);
      toast.success("Cluster saved & portfolio recalculated");
      refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await client.post("/nirsal/parse-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${r.data.field_count} clusters from upload`);
    } catch {
      toast.error("Upload parse failed");
    }
  };

  const F = ({ label, field, type = "text", opts = null }) => (
    <div className="mb-3">
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {opts ? (
        <select value={form[field] || ""} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 bg-white focus:outline-none focus:border-blue-400">
          <option value="">— select —</option>
          {opts.map(o => <option key={typeof o === "object" ? o.v : o} value={typeof o === "object" ? o.v : o}>{typeof o === "object" ? o.l : o}</option>)}
        </select>
      ) : (
        <input type={type} value={form[field] || ""} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
          className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:border-blue-400" />
      )}
    </div>
  );

  const Section = ({ title, children }) => (
    <div className="mb-4">
      <button className="w-full flex items-center justify-between text-xs font-bold text-[#1e3a5f] py-2 border-b border-slate-100"
        onClick={() => setExpanded(e => e === title ? null : title)}>
        <span>{title}</span>
        {expanded === title ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {(expanded === title || expanded === null) && <div className="pt-3">{children}</div>}
    </div>
  );

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen">
      <div className="flex items-center justify-between mb-5">
        <div>
          <PageHeader label="PORTFOLIO REPORTING" title="SSOT Input & Intelligence" />
          <p className="text-xs text-slate-400">Primary data entry — feeds all downstream pages</p>
        </div>
        <select value={selectedPeriod || ""} onChange={e => setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white">
          {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 mb-5">
        {["manual","upload"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
            className={`text-xs font-semibold px-4 py-2 rounded-lg border transition-all ${tab === t ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
            {t === "manual" ? "Manual Input" : "Bulk Upload"}
          </button>
        ))}
      </div>

      {tab === "upload" ? (
        <SectionCard title="Excel / CSV Bulk Upload">
          <div className="p-6 text-center space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-10">
              <Upload size={28} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 mb-3">Drag & drop Excel/CSV or click to browse</p>
              <label className="cursor-pointer bg-[#1e3a5f] text-white text-xs px-4 py-2 rounded-lg">
                Browse Files <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="upload-input" />
              </label>
            </div>
            <button className="flex items-center gap-2 text-xs text-blue-600 hover:underline mx-auto" data-testid="download-template">
              <Download size={12} /> Download Template (.xlsx)
            </button>
            <p className="text-xs text-slate-400">Supported columns: name, state, commodity, technology, capital_mobilized, reduction_tco2e_yr, crg_coverage, pathway, status, beneficiaries</p>
          </div>
        </SectionCard>
      ) : (
        <div className="grid grid-cols-5 gap-4">
          {/* Cluster selector */}
          <div className="col-span-1">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-xs font-bold text-slate-600">Clusters</div>
              {clusters.map(c => (
                <button key={c.cluster_id} onClick={() => selectCluster(c.cluster_id)}
                  className={`w-full text-left px-3 py-2.5 text-xs border-b border-slate-50 transition-all ${activeCluster === c.cluster_id ? "bg-[#1e3a5f] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                  <p className="font-semibold truncate">{c.name}</p>
                  <p className={`text-[10px] mt-0.5 ${activeCluster === c.cluster_id ? "text-blue-200" : "text-slate-400"}`}>{c.state} · {(c.status || "").title}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-5 overflow-y-auto max-h-[75vh]">
            <Section title="1. Project Identity">
              <F label="Cluster / Project Name" field="name" />
              <F label="State" field="state" />
              <F label="Commodity" field="commodity" />
              <F label="Technology / Intervention" field="technology" opts={TECHNOLOGIES.map(t => ({v:t,l:TECH_LABELS[t]||t}))} />
              <F label="Borrower Type" field="borrower_type" opts={["Cooperative / Farmer Group","SME / Processor","Individual Farmer","State Institution","NGO / Social Enterprise"]} />
              <F label="Partner Financial Institution (PFI)" field="pfi_name" />
            </Section>
            <Section title="2. Financing">
              <F label="Financing Product" field="financing_product" opts={["CSA Equipment Loan","Processor Energy Transition Loan","Working Capital (Green)","Asset Finance / Pay-as-you-save"]} />
              <F label="Capital Requested (USD)" field="capital_requested" type="number" />
              <F label="Capital Mobilized (USD)" field="capital_mobilized" type="number" />
              <F label="CRG Coverage (%)" field="crg_coverage_pct" type="number" />
              <F label="Interest Rebate (%)" field="interest_rebate_pct" type="number" />
              <F label="Status" field="status" opts={STATUSES} />
            </Section>
            <Section title="3. Climate & Output Data">
              <F label="Expected Reduction / Removal (tCO₂e/yr)" field="reduction_tco2e_yr" type="number" />
              <F label="Waste Processed (t/yr)" field="waste_processed_t_yr" type="number" />
              <F label="Renewable Energy (kWh/yr)" field="renewable_kwh_yr" type="number" />
              <F label="Water Stewardship (kl/yr)" field="water_kl_yr" type="number" />
              <F label="Plastic Recovered (t/yr)" field="plastic_recovered_t_yr" type="number" />
              <F label="Beneficiaries" field="beneficiaries" type="number" />
              <F label="Jobs Created" field="jobs_created" type="number" />
              <F label="Women/Youth Participation (%)" field="women_youth_pct" type="number" />
            </Section>
            <Section title="4. Carbon / Registry Pathway">
              <F label="Pathway Candidate" field="pathway" opts={PATHWAYS} />
              <F label="PoA / CPA Group Tag" field="poa_group" />
              <F label="DMRV Readiness" field="dmrv_readiness" opts={["low","medium","high"]} />
              <F label="Evidence Level" field="evidence_level" opts={["low","medium","high"]} />
            </Section>
            <div className="flex gap-2 mt-4">
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-2 bg-[#1e3a5f] text-white text-xs px-4 py-2 rounded-lg hover:bg-blue-900 transition-all disabled:opacity-60"
                data-testid="save-calculate-btn">
                {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                {saving ? "Saving…" : "Save & Calculate"}
              </button>
            </div>
          </div>

          {/* Live intelligence rail */}
          <div className="col-span-2 space-y-3">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-bold text-[#1e3a5f] mb-3">Live Portfolio Intelligence</p>
              {[
                ["Total Reduction", `${(portfolio.total_reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr`],
                ["Capital Mobilized", fmtU(portfolio.total_capital_mobilized)],
                ["CRG Exposure", fmtU(portfolio.total_crg_exposure)],
                ["Leverage Ratio", `${(portfolio.leverage_ratio||0).toFixed(1)}x`],
                ["Beneficiaries", (portfolio.total_beneficiaries||0).toLocaleString()],
                ["States Active", (portfolio.states_active||[]).join(", ") || "—"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-1.5 border-b border-slate-50">
                  <span className="text-slate-500">{k}</span>
                  <span className="font-bold text-slate-800">{v}</span>
                </div>
              ))}
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-xs font-bold text-emerald-700 mb-2">Pathway Composition</p>
              {[
                [`Art.6.4 (${portfolio.art6_4_clusters||0} clusters)`, portfolio.art6_4_reduction, "#1e3a5f"],
                [`VCM (${portfolio.vcm_clusters||0} clusters)`, portfolio.vcm_reduction, "#059669"],
                [`Plastic (${portfolio.plastic_clusters||0} clusters)`, portfolio.plastic_reduction, "#8b5cf6"],
              ].map(([l,v,c]) => (
                <div key={l} className="flex justify-between text-xs py-1">
                  <span style={{color:c}} className="font-medium">{l}</span>
                  <span className="text-slate-600">{fmtN(v, "tCO₂e/yr")}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-700 mb-2">Methodology Basis</p>
              {[
                ["Biodigester","AMS-III.D / AMS-III.R"],
                ["Rice Wastewater","AMS-III.R (methane)"],
                ["Solar","AMS-I.A / ACM0002"],
                ["Composting","AMS-III.E / AMS-I.C"],
                ["Plastic Credits","Verra Plastic Standard"],
                ["Framework","IPCC 2006 · PACM Art.6.4"],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between text-xs py-0.5">
                  <span className="text-slate-500">{l}</span>
                  <span className="font-medium text-blue-800 text-[10px]">{v}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
              <p className="text-xs font-bold text-blue-700 mb-2">Downstream Pages</p>
              {[
                ["Dashboard", "/corporate/nirsal/dashboard"],
                ["Value Bridge", "/corporate/nirsal/value-bridge"],
                ["Carbon Strategy", "/corporate/nirsal/carbon-strategy"],
                ["Carbon Structuring", "/corporate/nirsal/carbon-structuring"],
                ["Financials", "/corporate/nirsal/financials"],
                ["Reports", "/corporate/nirsal/reports"],
              ].map(([l, r]) => (
                <button key={l} onClick={() => navigate(r)} className="block w-full text-left text-xs text-blue-600 hover:underline py-0.5" data-testid={`nav-${l.toLowerCase().replace(" ","-")}`}>{l} →</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
